import {
    ButtonBase,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {ReactNode, useState} from "react";
import './OrangeOffer.scss';
import OrangeContactForm from "./OrangeContactForm";
import {CloseIcon, ZoomInIcon} from "./OrangeIcons";
import flyer from './oferta-orange.webp';
import logo from '../services/icons/orange.svg';

const tariffFeatures: ReactNode[] = [
    <><strong>Nielimitowane połączenia</strong> do wszystkich numerów komórkowych i stacjonarnych w Polsce</>,
    <><strong>Nielimitowane SMS i MMS</strong> na numery komórkowe w Polsce</>,
    <><strong>20,48 GB</strong> danych internetowych na każdym numerze; takt 1 kB</>,
    <>Transmisja danych w technologii <strong>5G</strong></>,
    <>Umowa lojalnościowa na <strong>24 miesiące</strong></>,
];

const zones = [
    {name: "Strefa 1", calls: <>odebrane 0,00 zł<br/>wykonane 0,00 zł</>, sms: "0,00 zł", mms: "0,00 zł", data: "0,01 zł"},
    {name: "Strefa 2", calls: "0,06 zł", sms: "0,00 zł", mms: "0,00 zł", data: "—"},
    {name: "Strefa 3", calls: "0,85 zł", sms: "0,55 zł", mms: "2,55 zł", data: "—"},
    {name: "Strefa 4", calls: "0,85 zł", sms: "0,55 zł", mms: "2,55 zł", data: "—"},
];

const internetPackages = [
    {size: "50 GB", price: "13,00 zł"},
    {size: "100 GB", price: "15,00 zł"},
    {size: "200 GB", price: "22,00 zł"},
    {size: "Bez limitu", price: "29,00 zł"},
];

const cyberWatchExtras = [
    "identyfikacja zainfekowanych urządzeń korzystających z sieci Orange",
    "blokowanie podejrzanego ruchu sieciowego z urządzeń stacjonarnych i mobilnych — informacja o zagrożeniach",
    "zapobieganie wyciekowi firmowych danych",
    "brak instalacji po stronie Klienta",
    "największy zespół ekspertów cyberbezpieczeństwa CERT w Polsce",
];

const contacts = [
    {name: "Anna Orzeł", email: "anna.orzel@orange.com", phone: "+48 501 137 253"},
    {name: "Magdalena Sumińska", email: "magdalena.suminska.opiekun@orange.com"},
    {name: "Marek Wojnarowicz", email: "marek.wojnarowicz.obsluga@orange.com"},
];

function Section(props: {title: string, badge?: ReactNode, children: ReactNode}) {
    return (
        <section className="OrangeOfferSection">
            <div className="OrangeOfferSectionHeader">
                <Typography variant="h6" component="h3">{props.title}</Typography>
                {props.badge && <span className="OrangeOfferBadge">{props.badge}</span>}
            </div>
            {props.children}
        </section>
    )
}

function OrangeOfferDialog(props: {open: boolean, onClose: () => void}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [lightboxOpen, setLightboxOpen] = useState(false);

    return (
        <>
            <Dialog
                open={props.open}
                onClose={props.onClose}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                scroll="paper"
                className="OrangeOfferDialog"
                aria-labelledby="orange-offer-title"
            >
                <DialogTitle id="orange-offer-title" className="OrangeOfferDialogTitle">
                    <img src={logo} alt="Orange" className="OrangeOfferDialogLogo"/>
                    <span>Oferta Orange dla parafii</span>
                    <IconButton aria-label="Zamknij" onClick={props.onClose} className="OrangeOfferDialogClose">
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers className="OrangeOffer">
                    <div className="OrangeOfferHero">
                        <div className="OrangeOfferHeroText">
                            <Typography className="OrangeOfferEyebrow">
                                Dedykowana oferta telefonii komórkowej i mobilnego Internetu
                            </Typography>
                            <Typography variant="h4" component="h2" className="OrangeOfferHeadline">
                                Możesz mieć tanie numery służbowe <span>dla instytucji kościelnych</span>, osób duchownych
                                i konsekrowanych
                            </Typography>
                            <Typography className="OrangeOfferAgreement">
                                Do <strong>Porozumienia z 27.01.2017 r.</strong>, wraz z późniejszymi aneksami
                                i zmianami taryf
                            </Typography>
                        </div>
                        <ButtonBase
                            className="OrangeOfferFlyer"
                            onClick={() => setLightboxOpen(true)}
                            aria-label="Powiększ ulotkę oferty"
                        >
                            <img src={flyer} alt="Ulotka oferty Orange dla instytucji kościelnych"/>
                            <span className="OrangeOfferFlyerOverlay">
                                <ZoomInIcon fontSize="small"/> Zobacz ulotkę
                            </span>
                        </ButtonBase>
                    </div>

                    <div className="OrangeOfferWarning" role="note">
                        <strong>Uwaga!</strong> Oferta nie jest dostępna w salonach Orange SA ani u innych pracowników
                        Orange SA poza dedykowanymi.
                    </div>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                            <Section title="Abonament i taryfa No Limit">
                                <div className="OrangeOfferPlan">
                                    <div className="OrangeOfferPrice">
                                        <span className="OrangeOfferPriceValue">7 zł</span>
                                        <span className="OrangeOfferPriceVat">+ VAT</span>
                                        <span className="OrangeOfferPriceCaption">miesięcznie za jeden numer<br/>(bez aparatu telefonicznego)</span>
                                    </div>
                                    <ul className="OrangeOfferList">
                                        {tariffFeatures.map((feature, i) => <li key={i}>{feature}</li>)}
                                    </ul>
                                </div>
                            </Section>

                            <Section title="Stawki w strefach">
                                <TableContainer className="OrangeOfferTable">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Strefa</TableCell>
                                                <TableCell align="right">Minuta połączenia</TableCell>
                                                <TableCell align="right">SMS</TableCell>
                                                <TableCell align="right">MMS</TableCell>
                                                <TableCell align="right">1 MB danych</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {zones.map(zone =>
                                                <TableRow key={zone.name}>
                                                    <TableCell component="th" scope="row">{zone.name}</TableCell>
                                                    <TableCell align="right">{zone.calls}</TableCell>
                                                    <TableCell align="right">{zone.sms}</TableCell>
                                                    <TableCell align="right">{zone.mms}</TableCell>
                                                    <TableCell align="right">{zone.data}</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Typography className="OrangeOfferFootnote">
                                    Taktowanie połączeń we wszystkich strefach: 60:60. Strefa 1 obejmuje Unię Europejską oraz
                                    Gujanę Francuską (+594), Falklandy (+500), Gibraltar (+350) i San Marino (+378);
                                    zaokrąglenie sesji transmisji danych w Strefie 1 — 100 kB.
                                </Typography>
                            </Section>

                            <Section title="Internet mobilny" badge="internetowa karta SIM">
                                <div className="OrangeOfferPackages">
                                    {internetPackages.map(p =>
                                        <div className="OrangeOfferPackage" key={p.size}>
                                            <span className="OrangeOfferPackageSize">{p.size}</span>
                                            <span className="OrangeOfferPackagePrice">{p.price}</span>
                                            <span className="OrangeOfferPackageCaption">abonament miesięczny</span>
                                        </div>
                                    )}
                                </div>
                                <div className="OrangeOfferExtra">
                                    <Typography className="OrangeOfferExtraTitle">
                                        Dodatkowo: nielimitowane LTE — <strong>6 zł</strong>
                                    </Typography>
                                    <Typography>
                                        Po wykorzystaniu obecnego pakietu (20 GB) otrzymujesz dodatkowo 100 GB danych.
                                        Po ich wykorzystaniu transmisja danych spada do 1 Mb/s. Opcję można aktywować
                                        na dowolny okres, np. 1 miesiąc.
                                    </Typography>
                                </div>
                            </Section>

                            <Section title="Tarcza CyberWatch" badge={<>36 zł + VAT</>}>
                                <Typography className="OrangeOfferParagraph">
                                    Za jeden numer, na okres trwania umowy (24 miesiące). Ochrona urządzeń Klienta
                                    i informowanie o wykrytych próbach komunikacji ze stronami stanowiącymi zagrożenie
                                    dla sieci mobilnej oraz blokowanie takiej komunikacji.
                                </Typography>
                                <div className="OrangeOfferThreats">
                                    <div className="OrangeOfferThreat">
                                        <Typography className="OrangeOfferThreatTitle">Phishing</Typography>
                                        <Typography>
                                            Oszustwo w celu uzyskania cennych informacji: loginów, haseł, numerów kart
                                            kredytowych.
                                        </Typography>
                                    </div>
                                    <div className="OrangeOfferThreat">
                                        <Typography className="OrangeOfferThreatTitle">Malware</Typography>
                                        <Typography>
                                            Wykrywanie złośliwego oprogramowania, które utrudnia korzystanie z urządzeń:
                                            blokuje ekran, szyfruje pliki, blokuje działanie systemu.
                                        </Typography>
                                    </div>
                                </div>
                                <ul className="OrangeOfferList OrangeOfferListCompact">
                                    {cyberWatchExtras.map(extra => <li key={extra}>{extra}</li>)}
                                </ul>
                            </Section>

                            <div className="OrangeOfferNotes">
                                <Typography><strong>Uwaga:</strong> podane ceny są cenami netto — w rozliczeniu każdej
                                    usługi należy doliczyć podatek VAT 23%.</Typography>
                                <Typography>Wykaz państw należących do poszczególnych stref znajduje się na stronie
                                    internetowej Orange.</Typography>
                            </div>

                            <Section title="Kontakt z opiekunami Orange">
                                <div className="OrangeOfferContacts">
                                    {contacts.map(c =>
                                        <div className="OrangeOfferContact" key={c.email}>
                                            <Typography className="OrangeOfferContactName">{c.name}</Typography>
                                            <a href={`mailto:${c.email}`}>{c.email}</a>
                                            {c.phone && <a href={`tel:${c.phone.replace(/\s/g, "")}`}>{c.phone}</a>}
                                        </div>
                                    )}
                                </div>
                            </Section>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <div className="OrangeOfferAside">
                                <OrangeContactForm/>
                            </div>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            <Dialog
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                maxWidth={false}
                className="OrangeOfferLightbox"
                aria-label="Ulotka oferty Orange"
            >
                <IconButton aria-label="Zamknij podgląd" onClick={() => setLightboxOpen(false)}
                            className="OrangeOfferLightboxClose">
                    <CloseIcon/>
                </IconButton>
                <img src={flyer} alt="Ulotka oferty Orange dla instytucji kościelnych"
                     onClick={() => setLightboxOpen(false)}/>
            </Dialog>
        </>
    )
}

export default OrangeOfferDialog;
